import { DeleteForever, MinorCrash } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { GreenText, OrderableTableCell } from 'components'
import { addVehiclesToOhioOutgoingTitle, removeVehiclesFromOhioOutgoingTitle } from 'controllers/ohioOutgoingTitles/index'
import { OhioOutgoingTitleDto, OhioOutgoingTitleVehicleDto } from 'dtos/ohioOutgoingTitles'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { clientSideOrderBy, formatDateMoment } from 'utils'
import AddVehiclesToAGroupDialog from './AddVehiclesToAGroupDialog'

interface ManageVehiclesCardProps {
  ohioOutgoingTitle: OhioOutgoingTitleDto
  setOhioOutgoingTitle: React.Dispatch<React.SetStateAction<OhioOutgoingTitleDto>>
}

export default function ManageVehiclesCard({ ohioOutgoingTitle, setOhioOutgoingTitle }: React.PropsWithChildren<ManageVehiclesCardProps>) {
  const [selectedVehicles, setSelectedVehicles] = useState<OhioOutgoingTitleVehicleDto[]>([])
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [isAddingVehicles, setIsAddingVehicles] = useState<boolean>(false)
  const [isRemovingVehicles, setIsRemovingVehicles] = useState<boolean>(false)

  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof OhioOutgoingTitleVehicleDto>('importerStockNumber')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)

  const onChangeSortByDirection = (propertyName: keyof OhioOutgoingTitleVehicleDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  return (
    <>
      <AddVehiclesToAGroupDialog
        ohioOutgoingTitleGuid={ohioOutgoingTitle.ohioOutgoingTitleGuid}
        isLoading={isAddingVehicles}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsAddingVehicles(true)
          addVehiclesToOhioOutgoingTitle(ohioOutgoingTitle.ohioOutgoingTitleGuid!, values)
            .then(res => {
              enqueueSnackbar('Vehicles Added Successfully!', { variant: 'success' })
              setOhioOutgoingTitle(res.value)
            })
            .finally(() => {
              setIsAddingVehicles(false)
              setDialogOpen(false)
            })
        }}
        open={dialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto' container spacing={1}>
                <Grid item>
                  <Typography fontWeight='bold' variant='h6'>
                    Manage Vehicles
                  </Typography>
                </Grid>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <LoadingButton
                  color='error'
                  disabled={selectedVehicles.length === 0}
                  endIcon={<DeleteForever />}
                  loading={isRemovingVehicles}
                  onClick={() => {
                    setIsRemovingVehicles(true)
                    removeVehiclesFromOhioOutgoingTitle(ohioOutgoingTitle.ohioOutgoingTitleGuid!, selectedVehicles)
                      .then(res => {
                        enqueueSnackbar('Vehicles Removed Successfully!', { variant: 'success' })
                        setOhioOutgoingTitle(res.value)
                        setSelectedVehicles([])
                        setPage(0)
                      })
                      .finally(() => {
                        setIsRemovingVehicles(false)
                      })
                  }}
                  variant='text'
                >
                  REMOVE SELECTED
                </LoadingButton>

                <Button color='primary' disabled={isRemovingVehicles} endIcon={<MinorCrash />} onClick={() => setDialogOpen(true)} variant='text'>
                  ADD VEHICLES
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} sx={{ width: '100%', overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox'>
                      <Checkbox
                        color='primary'
                        checked={selectedVehicles.length === ohioOutgoingTitle.vehicles?.length && selectedVehicles.length !== 0}
                        disabled={isRemovingVehicles || !ohioOutgoingTitle.vehicles?.length}
                        indeterminate={selectedVehicles.length > 0 && selectedVehicles.length !== ohioOutgoingTitle.vehicles?.length}
                        onChange={() => {
                          if (selectedVehicles.length === ohioOutgoingTitle.vehicles?.length) {
                            setSelectedVehicles([])
                          } else if (ohioOutgoingTitle?.vehicles) {
                            setSelectedVehicles(ohioOutgoingTitle.vehicles)
                          }
                        }}
                      />
                    </TableCell>

                    <OrderableTableCell<OhioOutgoingTitleVehicleDto>
                      keyName='importerStockNumber'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Importer Stock #
                    </OrderableTableCell>

                    <OrderableTableCell<OhioOutgoingTitleVehicleDto> keyName='vin' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      VIN
                    </OrderableTableCell>

                    <OrderableTableCell<OhioOutgoingTitleVehicleDto>
                      keyName='exporterCompany'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Exporter
                    </OrderableTableCell>

                    <OrderableTableCell<OhioOutgoingTitleVehicleDto>
                      keyName='buyerCompany'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Buyer
                    </OrderableTableCell>

                    <OrderableTableCell<OhioOutgoingTitleVehicleDto>
                      keyName='titleDestinationCompany'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Title Destination
                    </OrderableTableCell>

                    <OrderableTableCell<OhioOutgoingTitleVehicleDto> keyName='year' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Year
                    </OrderableTableCell>

                    <OrderableTableCell<OhioOutgoingTitleVehicleDto> keyName='make' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Make
                    </OrderableTableCell>

                    <OrderableTableCell<OhioOutgoingTitleVehicleDto> keyName='model' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Model
                    </OrderableTableCell>

                    <OrderableTableCell<OhioOutgoingTitleVehicleDto>
                      keyName='titleUploadedDateTime'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Title Uploaded
                    </OrderableTableCell>

                    <OrderableTableCell<OhioOutgoingTitleVehicleDto>
                      keyName='signedOutboundBOLUploadedDateTime'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Signed BOL
                    </OrderableTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {ohioOutgoingTitle?.vehicles
                    ?.sort(clientSideOrderBy<OhioOutgoingTitleVehicleDto>(orderBy, order))
                    .slice(page * pageSize, page * pageSize + pageSize)
                    ?.map((vehicle, index) => (
                      <TableRow key={vehicle.vin} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            color='primary'
                            checked={selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)}
                            onChange={() => {
                              if (selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)) {
                                setSelectedVehicles(selectedVehicles.filter(v => v.vehicleGuid !== vehicle.vehicleGuid))
                              } else {
                                setSelectedVehicles([...selectedVehicles, vehicle])
                              }
                            }}
                          />
                        </TableCell>

                        <TableCell>{vehicle.importerStockNumber}</TableCell>

                        <TableCell>{vehicle.vin}</TableCell>

                        <TableCell>{vehicle.exporterCompany}</TableCell>

                        <TableCell>{vehicle.buyerCompany}</TableCell>

                        <TableCell>{vehicle.titleDestinationCompany}</TableCell>

                        <TableCell>{vehicle.year}</TableCell>

                        <TableCell>{vehicle.make}</TableCell>

                        <TableCell>{vehicle.model}</TableCell>

                        <TableCell>{vehicle.titleUploadedDateTime ? formatDateMoment(vehicle.titleUploadedDateTime) : '-'}</TableCell>

                        <TableCell>{vehicle.signedOutboundBOLUploadedDateTime ? formatDateMoment(vehicle.signedOutboundBOLUploadedDateTime) : '-'}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      count={ohioOutgoingTitle.vehicles?.length ?? 0}
                      onPageChange={(_, page) => setPage(page)}
                      onRowsPerPageChange={e => setPageSize(e.target.value as unknown as number)}
                      page={page}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[5, 10, 25]}
                      SelectProps={{ fullWidth: false }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Manage Vehicles Card:**

Vehicles associated with this “Outgoing Titles Group” are displayed here. 

**Remove Selected Button:**

Clicking this button removes all selected vehicles from the Manage Vehicles Card.

This button is enabled only when at least one vehicle is selected.

**Add Vehicles Button:**

Clicking this button opens the Add Vehicles dialog.

**Select Box:**

Clicking the checkbox selects the vehicle in that row.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
